


import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { Newsletters } from "App";
import { EventBus } from "App/IOC";
import { NewsletterContact, NewsletterTag } from "App/Models/Newsletter";

/* Un tag s'affiche en minuscules et sans accents, comme au Desktop ( `TagLabel`,
   Fred 24/10/2026 ). */
function tagLabel( name: string ): string {
    return String( name || "" ).normalize( "NFD" ).replace( /[\u0300-\u036f]/g, "" ).toLowerCase();
}

interface PickerOption {
    Id: number;
    Label: string;
    Aside: string;
}

/* CHOISIR DES TAGS OU DES CONTACTS POUR LES DESTINATAIRES ( Console comme Desktop —
   Fred, 21/10/2026 : « pour les tags, garde ce principe de selection » — celui des destinataires
   des campagnes ).

   Le champ porte les choix en pastilles ( croix pour retirer ) et un chevron ;
   au clic, une liste s'ouvre dessous : recherche, titre du groupe, cases a
   cocher, lignes cochees surlignees. On coche plusieurs elements sans que la
   liste se ferme ; Maj + clic coche toute la plage depuis le dernier clic. La
   liste se ferme au clic a cote ou a Echap.

   ⚠️ PAS LE COMPOSANT DES CAMPAGNES LUI-MEME ( `GroupedAutocomplete` ) : il ouvre
   sa liste dans la couche des modales, SOUS le panneau lateral, et son champ
   depend des styles du formulaire de campagne. Meme principe, pose ici.

   Tags : la liste du compte, filtree sur place. Contacts : la recherche des
   campagnes, a partir de deux lettres ; un contact deja choisi dont on ne connait
   que le numero s'affiche « Contact n° … ». */
@Component
export default class RecipientPicker extends Vue {
    @Prop( { required: true } )
    readonly Id!: string;

    @Prop( { required: true } )
    readonly GroupId!: number;

    @Prop( { required: true } )
    readonly Kind!: "tags" | "contacts";

    @Prop( { default: () => [] } )
    readonly Value!: number[];

    @Prop( { default: () => [] } )
    readonly Tags!: NewsletterTag[];

    /* Le nom des contacts deja vus, par numero : partage entre les listes. */
    @Prop( { default: () => ( {} ) } )
    readonly Names!: { [ id: number ]: string };

    @Prop( { default: "" } )
    readonly Placeholder!: string;

    private open: boolean = false;

    /* La liste s'ouvre vers le HAUT quand la place manque dessous ( Fred,
       22/10/2026 : « la liste deroulante est tronquee, mets-la vers le haut » ). */
    private upward: boolean = false;
    private keyword: string = "";
    private timer: number = 0;
    private searching: boolean = false;
    private found: NewsletterContact[] = [];

    /* La derniere ligne cliquee : le depart d'une plage Maj + clic. */
    private anchor: number | null = null;

    mounted(): void {
        document.addEventListener( "mousedown", this.onOutside );
    }

    beforeDestroy(): void {
        document.removeEventListener( "mousedown", this.onOutside );
    }

    private onOutside( event: MouseEvent ): void {
        if( !this.$el.contains( event.target as Node ) )
            this.open = false;
    }

    /* La place libre sous le champ et au-dessus, dans le premier conteneur qui
       defile ( ou la fenetre ) : la liste prend le cote ou elle tient. */
    private place(): void {
        const root = this.$el as HTMLElement;
        const field = root.querySelector( ".v-recipient-picker__field" ) as HTMLElement | null;
        const panel = root.querySelector( ".v-recipient-picker__panel" ) as HTMLElement | null;

        if( field === null || panel === null )
            return;

        let top = 0;
        let bottom = window.innerHeight;
        let parent = root.parentElement;

        while( parent !== null ) {
            const overflow = window.getComputedStyle( parent ).overflowY;

            if( overflow === "auto" || overflow === "scroll" || overflow === "hidden" ) {
                const box = parent.getBoundingClientRect();

                top = Math.max( top, box.top );
                bottom = Math.min( bottom, box.bottom );
                break;
            }

            parent = parent.parentElement;
        }

        const rect = field.getBoundingClientRect();
        const below = bottom - rect.bottom;
        const above = rect.top - top;

        this.upward = below < panel.offsetHeight + 8 && above > below;
    }

    private toggleOpen(): void {
        this.open = !this.open;
        this.upward = false;

        if( this.open )
            this.$nextTick( (): void => this.place() );

        if( this.open )
            this.$nextTick( () => {
                const input = this.$refs.search as HTMLInputElement | undefined;

                if( input !== undefined )
                    input.focus();
            } );
    }

    private get groupName(): string {
        return this.$t(
            this.Kind === "tags" ? "NEWSLETTERS_groupTags" : "NEWSLETTERS_groupContacts"
        ).toString();
    }

    private get chosen(): PickerOption[] {
        return this.Value.map( id => ( { Id: id, Label: this.labelOf( id ), Aside: "" } ) );
    }

    private labelOf( id: number ): string {
        if( this.Kind === "tags" ) {
            const tag = this.Tags.find( item => item.id === id );

            return ( tag !== undefined ) ? tagLabel( tag.name ) : `#${ id }`;
        }

        return this.Names[ id ] || this.$t( "NEWSLETTERS_contactId", { id } ).toString();
    }

    /* Tags : tous, dans l'ordre du compte, filtres par la recherche. Contacts : les
       choisis, puis ceux de la recherche. */
    private get options(): PickerOption[] {
        if( this.Kind === "tags" ) {
            const keyword = this.keyword.trim().toLowerCase();

            const needle = tagLabel( keyword );

            return this.Tags
                .filter( tag => needle === "" || tagLabel( tag.name ).indexOf( needle ) >= 0 )
                .map( tag => ( { Id: tag.id, Label: tagLabel( tag.name ), Aside: "" } ) );
        }

        const others: PickerOption[] = this.found
            .filter( contact => this.Value.indexOf( contact.id ) < 0 )
            .map( contact => ( {
                Id: contact.id,
                Label: this.contactName( contact ),
                Aside: contact.email || ""
            } ) );

        return [ ...this.chosen, ...others ];
    }

    private get empty(): string {
        if( this.Kind === "contacts" && this.keyword.trim().length < 2 )
            return this.$t( "NEWSLETTERS_contactSearchHint" ).toString();

        return this.$t( "NEWSLETTERS_noResult" ).toString();
    }

    private contactName( contact: NewsletterContact ): string {
        const name = [ contact.firstname, contact.lastname ].filter( part => !!part ).join( " " );

        return name || contact.email || `#${ contact.id }`;
    }

    private isChecked( id: number ): boolean {
        return this.Value.indexOf( id ) >= 0;
    }

    /* Un clic coche ou decoche ; Maj + clic applique a toute la plage, depuis la
       derniere ligne cliquee, l'etat que prend la ligne cliquee. */
    private toggle( option: PickerOption, event: MouseEvent ): void {
        const checking = !this.isChecked( option.Id );
        const list = this.options;
        let targets: PickerOption[] = [ option ];

        if( event.shiftKey && this.anchor !== null ) {
            const from = list.findIndex( item => item.Id === this.anchor );
            const to = list.findIndex( item => item.Id === option.Id );

            if( from >= 0 && to >= 0 )
                targets = list.slice( Math.min( from, to ), Math.max( from, to ) + 1 );
        }

        this.anchor = option.Id;

        let values = [ ...this.Value ];

        targets.forEach( target => {
            const present = values.indexOf( target.Id ) >= 0;

            if( checking && !present ) {
                values = [ ...values, target.Id ];

                if( this.Kind === "contacts" )
                    this.$emit( "Named", target.Id, target.Label );
            } else if( !checking && present )
                values = values.filter( id => id !== target.Id );
        } );

        this.$emit( "Update", values );
    }

    private remove( id: number ): void {
        this.$emit( "Update", this.Value.filter( item => item !== id ) );
    }

    @Watch( "keyword" )
    private onKeyword(): void {
        if( this.Kind !== "contacts" )
            return;

        window.clearTimeout( this.timer );

        if( this.keyword.trim().length < 2 ) {
            this.found = [];
            return;
        }

        this.timer = window.setTimeout( () => this.search(), 300 );
    }

    private async search(): Promise<void> {
        const keyword = this.keyword.trim();

        this.searching = true;

        const [ contacts, error ] = await Newsletters.Contacts( this.GroupId, keyword, 8 );

        if( keyword !== this.keyword.trim() )
            return;

        this.searching = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.found = contacts || [];
    }
}
